import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaUser,
  FaBirthdayCake,
  FaMapMarkerAlt,
  FaFlag,
  FaHeart,
  FaChild,
  FaHome,
  FaEnvelope,
  FaIdCard,
  FaShieldAlt,
  FaUserCircle,
  FaIdBadge,
} from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import AvatarCircle from '../../components/Utils/AvatarCircle';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import MonProfilEnrichi from './MonProfilEnrichi';

/**
 * Ecran self-service des informations personnelles (contrat C6).
 *
 * Consomme GET /api/rh/moi via le proxy. Chaque champ vide dans la reponse C6
 * est rendu comme un tiret, jamais undefined ni null.
 */

const CHAMP_TIRET = '\u2014';

const champOuTiret = (val) => {
  if (val === null || val === undefined || val === '' || val === false) return CHAMP_TIRET;
  if (typeof val === 'number') return String(val);
  return String(val);
};

function MesInformations() {
  const { t } = useTranslation();
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [chargement, setChargement] = useState(true);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const res = await apiService.getElement('/rh/moi');
      const data = res.data;
      if (data && data.ok) {
        setDonnees(data.donnees);
      } else {
        setErreur(data?.message || t('mesInformations.erreurTechnique'));
      }
    } catch (e) {
      signalerErreur(e, 'MesInformations.charger');
      setErreur(t('mesInformations.erreurTechnique'));
    } finally {
      setChargement(false);
    }
  }, [t]);

  useEffect(() => {
    charger();
  }, [charger]);

  if (chargement) {
    return (
      <div className="mt-8" data-testid="mes-informations-loading">
        <LoadingBarWithStyles />
      </div>
    );
  }

  if (erreur) {
    return (
      <div
        className="mt-6 p-4 sm:p-5 bg-red-50/90 border border-red-200/80 rounded-2xl text-red-700 text-center shadow-sm"
        data-testid="mes-informations-error"
      >
        <p className="font-semibold text-sm sm:text-base">{erreur}</p>
      </div>
    );
  }

  if (!donnees) return null;

  const sections = [
    {
      id: 'identite',
      titre: t('mesInformations.sectionIdentite'),
      iconBox: 'bg-teal-500/10 text-teal-600',
      champs: [
        { cle: 'nom', icone: FaUser, label: t('mesInformations.nom') },
        { cle: 'date_naissance', icone: FaBirthdayCake, label: t('mesInformations.dateNaissance') },
        { cle: 'lieu_naissance', icone: FaMapMarkerAlt, label: t('mesInformations.lieuNaissance') },
        { cle: 'nationalite', icone: FaFlag, label: t('mesInformations.nationalite') },
      ],
    },
    {
      id: 'famille',
      titre: t('mesInformations.sectionFamille'),
      iconBox: 'bg-rose-500/10 text-rose-600',
      champs: [
        { cle: 'situation_familiale', icone: FaHeart, label: t('mesInformations.situationFamiliale') },
        { cle: 'nombre_enfants', icone: FaChild, label: t('mesInformations.nombreEnfants') },
      ],
    },
    {
      id: 'contact',
      titre: t('mesInformations.sectionContact'),
      iconBox: 'bg-blue-500/10 text-blue-600',
      champs: [
        { cle: 'adresse', icone: FaHome, label: t('mesInformations.adresse') },
        { cle: 'email', icone: FaEnvelope, label: t('mesInformations.email') },
      ],
    },
    {
      id: 'rh',
      titre: t('mesInformations.sectionRh'),
      iconBox: 'bg-amber-500/10 text-amber-600',
      // Yros/Kavero#488 : matricule et numéro de sécurité sociale, quel que
      // soit le pays. Le responsable a rejoint l'organigramme de « Mon équipe ».
      champs: [
        { cle: 'matricule', icone: FaIdCard, label: t('mesInformations.matricule') },
        { cle: 'numero_securite_sociale', icone: FaShieldAlt, label: t('mesInformations.numeroSecuriteSociale') },
      ],
    },
  ];

  const nomComplet = champOuTiret(donnees.nom);

  return (
    <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6" data-testid="mes-informations-content">
      {/* Bandeau d'accueil, même carte blanche que les sections dessous : un
          seul niveau de surface sur l'écran. L'accent de marque tient dans le
          filet vertical, pas dans un aplat coloré — c'est ce qui distingue le
          bandeau sans le faire crier. */}
      <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-100 flex items-center gap-4 sm:gap-5">
        <span className="w-1 self-stretch rounded-full bg-[--color-primary] flex-shrink-0" />
        <AvatarCircle
          name={nomComplet !== CHAMP_TIRET ? nomComplet : t('mesInformations.employe')}
          size={56}
        />
        {/* Le bandeau accueille, les cartes informent : email, CNPS et
            nationalité ne vivent que dans leur carte, une fois à l'écran. */}
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-slate-800 truncate min-w-0 flex-1">
          {t('mesInformations.bienvenue')} {nomComplet !== CHAMP_TIRET ? nomComplet : ''}
        </h2>
        <BoutonMonBadge />
      </div>

      <MonProfilEnrichi nom={nomComplet !== CHAMP_TIRET ? nomComplet : t('mesInformations.employe')} />

      {/* Grid of Section Cards (1 col on mobile 375px, 2 cols on tablet/desktop) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {sections.map((sec) => (
          <div
            key={sec.id}
            className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-100 hover:shadow-md transition-all duration-200 flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center mb-3.5 pb-2.5 border-b border-slate-100">
                <div
                  className={`w-8 h-8 rounded-xl flex items-center justify-center mr-3 font-semibold text-sm ${sec.iconBox}`}
                >
                  {sec.id === 'identite' && <FaUserCircle className="w-4 h-4" />}
                  {sec.id === 'famille' && <FaHeart className="w-4 h-4" />}
                  {sec.id === 'contact' && <FaHome className="w-4 h-4" />}
                  {sec.id === 'rh' && <FaIdCard className="w-4 h-4" />}
                </div>
                <h3 className="text-sm sm:text-base font-bold text-slate-800 tracking-tight">
                  {sec.titre}
                </h3>
              </div>

              <div className="space-y-2.5">
                {sec.champs.map(({ cle, icone: Icone, label }) => {
                  const val = champOuTiret(donnees[cle]);
                  return (
                    <div
                      key={cle}
                      className="group p-2.5 sm:p-3 rounded-xl bg-slate-50/70 hover:bg-slate-100/90 transition-colors flex items-start space-x-3 border border-slate-100/80"
                    >
                      <div className="w-8 h-8 rounded-lg bg-white shadow-2xs flex items-center justify-center text-slate-500 group-hover:text-[--color-primary] flex-shrink-0 mt-0.5 transition-colors">
                        <Icone className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] sm:text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">
                          {label}
                        </p>
                        <p className="text-xs sm:text-sm font-semibold text-slate-800 break-words">
                          {val}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Le badge du salarié (Yros/Kavero#488), le même PDF que celui de la RH.
 */
function BoutonMonBadge() {
  const { t } = useTranslation();
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(false);

  const telecharger = async () => {
    setEnCours(true);
    setErreur(false);
    try {
      const reponse = await apiService.getBlobFile('/rh/moi/badge');
      apiService.downloadBlob(new Blob([reponse.data], { type: 'application/pdf' }), 'mon-badge.pdf');
    } catch (e) {
      signalerErreur(e, 'MesInformations.telechargerBadge');
      setErreur(true);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1 flex-shrink-0">
      <button
        type="button"
        data-testid="mon-badge-telecharger"
        onClick={telecharger}
        disabled={enCours}
        aria-label={t('mesInformations.monBadge')}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] disabled:opacity-50 transition-colors"
      >
        <FaIdBadge className="w-4 h-4" aria-hidden="true" />
        <span className="hidden sm:inline">{t('mesInformations.monBadge')}</span>
      </button>
      {erreur && (
        <p data-testid="mon-badge-erreur" className="text-xs text-red-700">
          {t('mesInformations.erreurBadge')}
        </p>
      )}
    </div>
  );
}

export default MesInformations;

